// react
import { useEffect } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Box,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

// feature-hooks
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';
import useUrlViewMode from '@/features/energyxplorer/hooks/useUrlViewMode';

// denne deles PT af to portfolio analysis komponenter her i remote, men eksporteres også til containeren.
// bør derfor på sigt flyttes til et mere generelt sted, hvor den kan genbruges af flere komponenter i containeren.

type FilterState = {
  activePeriodFilter: boolean | undefined;
  activeDaysFilter: boolean | undefined;
  activeHolidaysFilter: boolean | undefined;
};

const PortfolioAnalysisFilterInput = ({
  handleApply, // still supported (optional now)
  showPeriodFilter = true,
  showDaysFilter = true,
  showHolidaysFilter = true,
}: {
  handleApply: (filters: FilterState) => void;
  showPeriodFilter?: boolean;
  showDaysFilter?: boolean;
  showHolidaysFilter?: boolean;
}) => {
  const { t } = useTranslation([
    'energyxplorer-analysis-tabs',
    'portfolio-analysis',
  ]);

  // URL-backed filters with sensible defaults
  const {
    filters,
    applyFilters, // shallow merge -> URL
  } = useUrlFilters({
    activePeriodFilter: undefined,
    activeDaysFilter: undefined,
    activeHolidaysFilter: undefined,
  });

  // Optionally read view mode from URL if prop not provided
  const [viewModeFromUrl] = useUrlViewMode({
    allowed: ['column', 'box', 'line'], // include 'line' since you check against it
    fallback: 'column',
  });

  // derived values for toggle groups
  const periodFilter = filters.activePeriodFilter; // true | false | undefined
  const daysFilter = filters.activeDaysFilter; // true | false | undefined
  const holidaysFilter = filters.activeHolidaysFilter; // true | false | undefined

  // keep parent callback behavior (backwards compatible)
  useEffect(() => {
    handleApply({
      activePeriodFilter: periodFilter,
      activeDaysFilter: daysFilter,
      activeHolidaysFilter: holidaysFilter,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, daysFilter, holidaysFilter]);

  // handlers write straight to the URL (and thus to shared state)
  const setPeriodFilter = (val: boolean | undefined) =>
    applyFilters({ activePeriodFilter: val });
  const setDaysFilter = (val: boolean | undefined) =>
    applyFilters({ activeDaysFilter: val });
  const setHolidaysFilter = (val: boolean | undefined) =>
    applyFilters({ activeHolidaysFilter: val });

  const renderToggleGroup = (
    label: string,
    value: boolean | undefined,
    setter: (val: boolean | undefined) => void,
    options: Array<{ label: string; value: string }>,
  ) => (
    <Box mb={2} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={stringify(value)}
        exclusive
        onChange={(_, newValue) => setter(parseNullable(newValue))}
        fullWidth
      >
        {options.map(({ label, value }) => (
          <ToggleButton
            key={value}
            value={value}
            disabled={viewModeFromUrl === 'line'}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <>
      <DialogContent>
        {showPeriodFilter &&
          renderToggleGroup(
            t('filters.active_period', { ns: 'portfolio-analysis' }),
            periodFilter,
            setPeriodFilter,
            [
              {
                label: t('filters.all', { ns: 'portfolio-analysis' }),
                value: '',
              },
              {
                label: t('filters.inside_operating_hours', {
                  ns: 'portfolio-analysis',
                }),
                value: 'true',
              },
              {
                label: t('filters.outside_operating_hours', {
                  ns: 'portfolio-analysis',
                }),
                value: 'false',
              },
            ],
          )}

        {showDaysFilter &&
          renderToggleGroup(
            t('filters.active_days', { ns: 'portfolio-analysis' }),
            daysFilter,
            setDaysFilter,
            [
              {
                label: t('filters.all', { ns: 'portfolio-analysis' }),
                value: '',
              },
              {
                label: t('filters.active_days_only', {
                  ns: 'portfolio-analysis',
                }),
                value: 'true',
              },
              {
                label: t('filters.inactive_days_only', {
                  ns: 'portfolio-analysis',
                }),
                value: 'false',
              },
            ],
          )}

        {showHolidaysFilter &&
          renderToggleGroup(
            t('filter_labels.holidays'),
            holidaysFilter,
            setHolidaysFilter,
            [
              {
                label: t('filters.all', { ns: 'portfolio-analysis' }),
                value: '',
              },
              {
                label: t('filters.holidays_only', { ns: 'portfolio-analysis' }),
                value: 'true',
              },
              {
                label: t('filters.no_holidays', { ns: 'portfolio-analysis' }),
                value: 'false',
              },
            ],
          )}
      </DialogContent>
      <DialogActions />
    </>
  );
};

const parseNullable = (val: string | null): boolean | undefined => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

const stringify = (val: boolean | undefined): string => {
  if (val === true) return 'true';
  if (val === false) return 'false';
  return '';
};

export default PortfolioAnalysisFilterInput;
